import { useCallback, useMemo, useState } from "react";

import { setSentiment } from "@/api/endpoints/episodes";
import { markIrrelevant, markRelevant } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { translate } from "@/api/endpoints/translate";
import { MENTION_TYPE, SOURCE } from "@/constants";
import clusterTimes from "@/containers/Episode/utils/clusterTimes";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useEpisodes } from "@/contexts/EpisodesContext";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import { events, getMentionContextTimings } from "@/utils";

import EpisodeContext from "./context";
import { LocalAudioProvider } from "./LocalAudioContext";
import ReaderModal from "./partials/modals/ReaderModal";
import TiktokModal from "./partials/modals/TikTokModal";
import YoutubeModal from "./partials/modals/YoutubeModal";

/**
 * Group all mentions in sub-categories to be easier to use for the underlying components.
 */

const EpisodeProvider = ({
  children,
  mentions = [],
  episode_uuid,
  ...props
}) => {
  // States
  // regular mentions.
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [readerModalOpen, setReaderModalOpen] = useState(false);
  const [removedMentionIds, setRemovedMentionIds] = useState([]);
  const [removedSource, setRemovedSource] = useState(false);

  // Contexts
  const { language } = useI18n();
  const { danger } = useMessages();
  const { updateMention, patchEpisode } = useEpisodes();

  // Sentiment is somehow being fiddled with - Highlight it in the timeline.
  const [sentimentActive, setSentimentActive] = useState(false);

  // Translates a text of the mention with the given uuid and then renders it.
  const translateMention = (
    mention,
    toKey = "amended_text",
    fromKey = "trimmed_content"
  ) => {
    if (mention.translated_text) {
      updateMention({
        ...mention,
        [toKey]: mention.translated_text,
        translated: true,
      });
    } else {
      translate({
        text: mention[fromKey],
        target_language: language,
      }).then((res) => {
        if (res.ok && res.data) {
          postTrackingData(EVENT.MENTION_TRANSLATED, {
            from_language: res.data.original_language,
            to_language: res.data.target_language,
          });
          updateMention({
            ...mention,
            [toKey]: res.data.translated_text,
            translated_text: res.data.translated_text,
            original_text: mention[fromKey],
            translated: true,
          });
        }
      });
    }
  };

  const unTranslateMention = (mention, toKey = "amended_text") => {
    updateMention({
      ...mention,
      [toKey]: mention.original_text,
      translated: false,
    });
  };

  const removeMention = useCallback(
    (ids = [], reason = null) => {
      // Add it to the list of removed mentions!
      setRemovedMentionIds((old) => Array.from(new Set([...old, ...ids])));
      return markIrrelevant(ids, { reason })
        .then((res) => {
          if (!res.ok) {
            // Something went wrong. revert the change.
            setRemovedMentionIds((old) =>
              old.filter((id) => !ids.includes(id))
            );
          }
        })
        .catch((error) => {
          danger({
            title: L.CUSTOMERS_SEARCH_500_ERROR_TITLE,
            message: L.CUSTOMERS_SEARCH_500_ERROR_MESSAGE,
          });

          throw error;
        });
    },
    [danger]
  );

  const undoRemoveMention = useCallback((ids, unhide_source = false) => {
    // Remove the ids from the removed list.
    setRemovedMentionIds((old) => old.filter((id) => !ids.includes(id)));
    if (unhide_source) setRemovedSource(false);
    return markRelevant(ids, unhide_source).then((res) => {
      if (!res.ok) {
        //oh no! something went wrong. revert the change.
        setRemovedMentionIds((old) => [...old, ...ids]);
      }
    });
  }, []);

  const removeAll = useCallback(() => {
    // Stop any playing audio before removing mentions.
    document.dispatchEvent(events.stopAllPlayingAudio);

    return removeMention(mentions.map((m) => m.public_id));
  }, [mentions, removeMention]);

  const removeSource = useCallback(() => {
    // Stop any playing audio before removing mentions.
    document.dispatchEvent(events.stopAllPlayingAudio);
    setRemovedSource(true);
    return removeMention(
      mentions.map((m) => m.public_id),
      "excluded_source"
    );
  }, [removeMention, mentions]);

  const removeIndividual = useCallback(
    (currentId) => {
      // Stop any playing audio before removing mentions.
      document.dispatchEvent(events.stopAllPlayingAudio);
      return removeMention([currentId]);
    },
    [removeMention]
  );

  const audioMentions = useMemo(
    () =>
      mentions
        .filter((m) => m.context === MENTION_TYPE.TEXT)
        .map((m) => {
          if (!props.grouped_times_and_words)
            return {
              ...m,
              context_start_timing: m.timing,
              context_end_timing: Math.min(props.total_length, m.timing + 30),
            };
          // If the mention text has been translated, use the original text
          // since otherwise we won't find the mention timings correctly.
          const mentionText = m.translated
            ? m.original_text
            : m.trimmed_content;
          const { start, end } = getMentionContextTimings({
            words_with_times: props.grouped_times_and_words,
            mention_text: mentionText,
          });
          return { ...m, context_start_timing: start, context_end_timing: end };
        }),
    [mentions, props.total_length, props.grouped_times_and_words]
  );

  // Text mentions
  const textMentions = useMemo(
    () => mentions.filter((m) => m.context === MENTION_TYPE.TEXT_POST),
    [mentions]
  );

  // Episode name mentions
  const episodeNameMentions = useMemo(
    () => mentions.filter((f) => f.context === MENTION_TYPE.EPISODE_NAME),
    [mentions]
  );

  // Episode description mentions
  const episodeDescriptionMentions = useMemo(
    () =>
      mentions.filter((f) => f.context === MENTION_TYPE.EPISODE_DESCRIPTION),
    [mentions]
  );

  // Channel name mentions
  const channelTitleMentions = useMemo(
    () => mentions.filter((f) => f.context === MENTION_TYPE.CHANNEL_TITLE),
    [mentions]
  );

  const clusters = useMemo(
    () => clusterTimes(props.grouped_times_and_words?.times || []),
    [props.grouped_times_and_words]
  );

  const updateMentionSentiment = useCallback(
    (mention, sentiment) => {
      setSentiment([mention.public_id], sentiment)
        .then((res) => {
          // Patch the sentiment
          if (res.ok) {
            const { aggregated_sentiment, insights } = res.data;
            updateMention({ ...mention, sentiment });
            patchEpisode({
              episode_uuid,
              aggregated_sentiment: aggregated_sentiment,
              insights,
            });
          }
        })
        .catch(() => {
          const errorMessage = L.CUSTOMERS_SEARCH_500_ERROR;
          danger({
            title: errorMessage.title,
            message: errorMessage.message,
          });
        });
    },
    [updateMention, patchEpisode, episode_uuid, danger]
  );

  const updateAggregatedSentiment = useCallback(
    (sentiment) => {
      setSentiment(
        mentions.map((m) => m.public_id),
        sentiment
      ).then((res) => {
        if (res.ok) {
          const { aggregated_sentiment, insights } = res.data;
          patchEpisode({
            episode_uuid,
            aggregated_sentiment: aggregated_sentiment,
            insights,
          });
        }
        mentions.map((m) => updateMention({ ...m, sentiment }));
      });
    },
    [patchEpisode, mentions, updateMention, episode_uuid]
  );

  return (
    <EpisodeContext.Provider
      value={{
        ...props,
        episode_uuid,
        mentions,
        audioMentions,
        textMentions,
        episodeNameMentions,
        episodeDescriptionMentions,
        channelTitleMentions,
        clusters,
        mediaModalOpen,
        setMediaModalOpen,
        readerModalOpen,
        setReaderModalOpen,
        removeAll,
        removedSource,
        removeIndividual,
        removeSource,
        removedMentionIds,
        undoRemoveMention,
        updateMentionSentiment,
        updateAggregatedSentiment,
        sentimentActive,
        setSentimentActive,
        translateMention,
        unTranslateMention,
      }}
    >
      <LocalAudioProvider>{children}</LocalAudioProvider>
      {props.source === SOURCE.TIKTOK && (
        <TiktokModal
          onHide={() => setMediaModalOpen(false)}
          show={mediaModalOpen}
        />
      )}
      {props.source === SOURCE.YOUTUBE && (
        <YoutubeModal
          onHide={() => setMediaModalOpen(false)}
          show={mediaModalOpen}
        />
      )}
      {Boolean(props.source === SOURCE.NEWS && props.article_text) && (
        <ReaderModal
          show={readerModalOpen}
          onHide={() => setReaderModalOpen(false)}
        />
      )}
    </EpisodeContext.Provider>
  );
};

export default EpisodeProvider;
